import React, {useContext} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {css} from '@emotion/react'
import styled from '@emotion/styled'
import {format, parseISO} from 'date-fns'

import {Button} from '@daedalus/atlas/src/components/designSystem/Button'
import ContentWrapper from '@daedalus/atlas/src/components/designSystem/helpers/ContentWrapper'
import {Icon} from '@daedalus/atlas/src/components/designSystem/Icon'
import {Text} from '@daedalus/atlas/src/components/designSystem/Text'

import {ExternalActionsContext} from '../../context/ExternalActionsContext'
import {
  getApplication,
  getError,
  getFreezeSelectedContext
} from '../../modules/selector'
import {
  ApplicationNames,
  closeAllFreezeOverlays,
  closeFreezeOverlay
} from '../../modules/slice'
import {ErrorCases} from './types'
import {useGetErrorMessageAndCase} from './useGetErrorMessageAndCase'
import {useTrackApiError} from './useTrackApiError'

const formatDate = (date: string) => {
  return format(parseISO(date), 'MMM dd')
}

const Container = styled(ContentWrapper)(
  ({theme}) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.layout.spacing.s200}px;
  `
)

const MessageWrapper = styled.div(
  ({theme}) => css`
    display: flex;
    align-items: center;
    gap: ${theme.layout.spacing.s200}px;
  `
)

const StyledButton = styled(Button)(
  ({theme}) => css`
    margin-top: ${theme.layout.spacing.s300}px;
  `
)

const StyledIcon = styled(Icon)(
  ({theme}) => css`
    color: ${theme.colors.content.danger.c500};
  `
)

export const ErrorMessage = () => {
  const dispatch = useDispatch()
  const {name: application} = useSelector(getApplication)
  const {openSearchBox, setIsSearchBoxActive} = useContext(
    ExternalActionsContext
  )
  const {offer, searchDetail} = useSelector(getFreezeSelectedContext)
  const {rspUrl} = offer || {}
  const {checkIn, checkOut} = searchDetail || {}

  const error = useSelector(getError)
  const {errored} = error || {}

  const {formatMessage} = useIntl()
  const {title, message, ctaText, icon, errorCase} = useGetErrorMessageAndCase({
    error
  })

  // when errorMessage component is shown with all the required data, error should be tracked
  useTrackApiError({error, errorCase})

  if (!errored) return null

  const checkInDate = checkIn && formatDate(checkIn)
  const checkOutDate = checkOut && formatDate(checkOut)
  const formattedTitle = formatMessage(title)
  const formattedMessage = formatMessage(message, {
    checkIn: checkInDate,
    checkOut: checkOutDate
  })
  const formattedCtaText = formatMessage(ctaText)

  const changeRoom = () => {
    if (rspUrl && application == ApplicationNames.Search) {
      window.open(rspUrl, '_blank')
    }

    dispatch(closeFreezeOverlay())
  }

  const ctaFunctions = {
    [ErrorCases.SoldOut]: changeRoom,
    [ErrorCases.Unavailability]: () => {
      dispatch(closeFreezeOverlay())
      dispatch(openSearchBox('page'))

      if (application == ApplicationNames.Search && setIsSearchBoxActive) {
        dispatch(setIsSearchBoxActive(true))
      }

      if (application == ApplicationNames.Checkout) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
      }
    },
    [ErrorCases.LimitReached]: () => {
      dispatch(closeFreezeOverlay())
    },
    [ErrorCases.UnexpectedError]: () => {
      dispatch(closeAllFreezeOverlays())
    }
  } as Record<ErrorCases, () => void>

  const onClick = ctaFunctions[errorCase]

  return (
    <Container>
      <MessageWrapper>
        <StyledIcon name={icon} />
        <Text variant="titleM">{formattedTitle}</Text>
      </MessageWrapper>
      <Text>{formattedMessage}</Text>
      <StyledButton variant="secondary" fullWidth onClick={onClick}>
        {formattedCtaText}
      </StyledButton>
    </Container>
  )
}
